import React, { ReactNode, useState, useEffect } from 'react';
import Carousel from '../Carousel/carousel';
import add from 'date-fns/add';
import sub from 'date-fns/sub';
import AnimatedModal from '../AnimatedModal/animatedModal';
import { AnimateFrom } from '../AnimatedModal/animatedModal.types';
import ReactDatePicker from 'react-datepicker';
import { MobileDatePickerProps } from './datePicker.types';
import CalendarSkeleton from './mobileDatePickerSkeleton';
import { MobileDateButtonWrapper, DateLabel, MobileWrapper } from './datePicker.style';
import { default as ReactCarousel } from 'react-multi-carousel';

const carouselLayout = {
  universal: {
    breakpoint: { max: 100000, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let carouselRef: any = null; // A reference to the carousel for setting current slide

// holds our slides of datepicker components
let slides: ReactNode[] = [];

const MobileDatePicker = (props: MobileDatePickerProps) => {
  const {
    CalendarIcon,
    DatePickerWrapperComponent,
    TodayButton,
    activeDate,
    localSelectedDate,
    setActiveDate,
    combinedHandlers,
    mobileModalOpen,
    setMobileModalOpen,
    dataAttr,
    sharedWrapperProps,
    sharedDatePickerProps,
    YearPicker,
    MiniYearPicker,
    viewYear,
    setViewYear,
    showTodayButton,
    formattedLocalDate,
    showDateText = false,
  } = props;

  const [loading, setLoading] = useState(true); // Set false after we move to the initial slide
  const [locked, setLocked] = useState(false);

  // checks for modal closing so we can set loading state back to true for next open
  // has timeout since the set to false code has a timeout too (to wait for loading)
  useEffect(() => {
    if (!mobileModalOpen) {
      setTimeout(() => {
        setLoading(true);
      }, 500);
    }
  }, [mobileModalOpen]);

  let shiftAmount: number = 0; // Will use to keep track of which direction we swiped

  const { disabled } = sharedDatePickerProps;

  // Lock ability to change months briefly
  // to allow swiping to return
  const lockMonths = (length: number = 500) => {
    setLocked(true);
    setTimeout(() => {
      setLocked(false);
    }, length);
  };

  const beforeChange = (nextSlide: number) => {
    lockMonths(750);
    shiftAmount = nextSlide - 1; // Remember which direction we're swiping
  };

  const afterChange = () => {
    // checking whether the carousel change was forward or backward
    // then updating our `activeDate` which identifies the in focus datepicker

    const newDate = shiftAmount < 0 ? sub(activeDate, { months: 1 }) : add(activeDate, { months: 1 });
    slides = [...generateSlides(newDate)];
    setActiveDate(newDate);
    // programmatically moves the state of carouel back to index 1 (centered slide)
    carouselRef.goToSlide(1, {
      skipBeforeChange: true,
      skipAfterChange: true,
    });
  };

  // handler for clicking arrow on date picker
  const monthChangeHandler = (date: Date) => {
    slides = [...generateSlides(date)];
    setActiveDate(date);
  };

  // firing datepicker selection handler from parent and collapsing modal
  const datepickerChangeHandler = (date: Date) => {
    combinedHandlers.onChange(date);
    setMobileModalOpen(false);
  };

  const inlineDatePicker = (openToDate: Date, i: number) => (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    <DatePickerWrapperComponent key={`slide-${i}`} {...sharedWrapperProps} {...dataAttr}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <ReactDatePicker
        onChange={datepickerChangeHandler}
        onMonthChange={monthChangeHandler}
        inline={true}
        openToDate={openToDate}
        // using highlighted dates because using `selected` prop causes odd behavior with multiple instances of datepicker
        highlightDates={[localSelectedDate]}
        {...sharedDatePickerProps}
      />
    </DatePickerWrapperComponent>
  );

  const generateSlides = (localSelectedDate: Date, loading: boolean = false): ReactNode[] =>
    ['', '', ''].map((el, i) => {
      let openToDate: Date;
      if (i === 0) {
        openToDate = sub(localSelectedDate, { months: 1 });
      } else if (i === 1) {
        openToDate = localSelectedDate;
      } else {
        openToDate = add(localSelectedDate, { months: 1 });
      }
      return loading ? CalendarSkeleton(i) : inlineDatePicker(openToDate, i);
    });

  slides = generateSlides(viewYear || activeDate, loading);

  // generate a ref for programmatic changes to carousel
  const setCarouselRef = (el: ReactCarousel) => {
    if (el && el.state) {
      carouselRef = el;
      el.state.currentSlide = 1;

      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const closeModalHandler = {
    onClosed: () => {
      setMobileModalOpen(false);
      setActiveDate(localSelectedDate);
      setViewYear(undefined);
    },
  };

  const onOpenModalHandler = {
    onClick: () => !disabled && setMobileModalOpen(!mobileModalOpen),
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.which === 13 && !disabled) {
        setMobileModalOpen(!mobileModalOpen);
      }
    },
  };

  return (
    <MobileWrapper>
      <div
        role={'button'}
        tabIndex={0}
        className="react-datepicker-wrapper mobile-datePIcker-button"
        {...onOpenModalHandler}
        {...dataAttr}
      >
        {showDateText ? (
          <MobileDateButtonWrapper>
            {CalendarIcon}
            <DateLabel>{formattedLocalDate}</DateLabel>
          </MobileDateButtonWrapper>
        ) : (
          CalendarIcon
        )}
      </div>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <AnimatedModal
        overlay={true}
        open={mobileModalOpen}
        fullScreenMobile={false}
        width="100%"
        // bit on the high side, but needs to cover for iphone devices w/ fucking safari bottom nav + max height for small devices
        height="470px; max-height: 90%"
        animate={true}
        animateFrom={AnimateFrom.BOTTOM}
        position={{ bottom: 0, left: 0 }}
        handlers={closeModalHandler}
        customHeader={<React.Fragment></React.Fragment>}
        // Add if the months are currently locked or not
        className={`mobileDatePickerModal ${locked ? 'month-locked' : 'month-unlocked'}`}
      >
        <Carousel
          layout={carouselLayout}
          showArrows={false}
          afterChange={afterChange}
          beforeChange={beforeChange}
          refHandler={setCarouselRef}
          customTransition="none"
        >
          {slides}
        </Carousel>
        {MiniYearPicker}
        {YearPicker}
        {showTodayButton ? TodayButton : null}
      </AnimatedModal>
    </MobileWrapper>
  );
};

export default MobileDatePicker;
